/**
 * @copyright Copyright 2022-2025 Epic Systems Corporation
 * @file Error list for error oject. Uses react form hook error object
 * @module Epic.AppOrchard.Core.ErrorList
 */

import { Text, TextProps } from "@chakra-ui/react";
import { ErrorText } from "ao/components/Core/ErrorText";
import React, { FC } from "react";
import { FieldErrors } from "react-hook-form/dist/types/errors";

interface IErrorListProps {
	errorObject: FieldErrors;
	basePath?: string;
}

type IProps = TextProps & IErrorListProps;

// Helper function to render errors recursively
const renderErrors = (errors: any, basePath: string = ""): React.ReactNode[] => {
	return Object.entries(errors).map(([key, value]) => {
		const currentPath = basePath ? `${basePath}.${key}` : key;

		// Check if value is an error with a message
		if (value && typeof value === "object" && "message" in value) {
			return (
				<ErrorText key={currentPath}>
					<Text>{(value as { message: string }).message}</Text>
				</ErrorText>
			);
		}

		// Check if value is an array of errors
		if (Array.isArray(value)) {
			return (
				<ErrorText key={currentPath}>
					{value
						.filter(
							(error: any, index: number, array: any[]) =>
								!!error &&
								array.map((err2) => (err2 ? err2.message : "")).indexOf(error.message) ===
									index,
						)
						.map((arrayError: any, index: number) => (
							<Text key={index}>{arrayError.message}</Text>
						))}
				</ErrorText>
			);
		}

		// If it's a nested object with potential errors
		if (value && typeof value === "object" && Object.keys(value).length > 0) {
			return renderErrors(value, currentPath);
		}

		return null;
	});
};

/*Error list for error object. Uses react form hook error object */
export const ErrorList: FC<IErrorListProps> = (props: IProps) => {
	const { errorObject, basePath = "" } = props;

	if (!errorObject || Object.keys(errorObject).length === 0) {
		return null;
	}

	return <>{renderErrors(errorObject, basePath)}</>;
};
